import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClaimAttemptCount } from '@/components/Students/ClaimAttemptCount';
import { cn } from '@/helpers/classnames';
import {
  CLAIM_STATES,
  findOtherTenantInClaimMessage,
  isClaimErrorState,
  SCHEDULED_DESCRIPTION,
  SCHEDULED_LABEL,
} from '@/helpers/claimStates';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import {
  claimStateRevokedBecauseStudentWillInactivate,
  ticketRevocationMessages,
} from '@/helpers/ticketRevocation';
import { useTenantID } from '@/hooks/useTenantID';
import { IconCheck } from '@/icons';
import { CollectionTicketClaimMemberStateDTOState } from '@/api/trafikportalen/models/collectionTicketClaimMemberStateDTOState';
import { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';

const ALL_STEPS = [
  'waiting',
  'card-ordered',
  'purchase-ordered',
  'fulfilled',
] as const;

type TimelineStep = (typeof ALL_STEPS)[number];

function stateToStepKey(
  state?: CollectionTicketClaimMemberStateDTOState,
): TimelineStep {
  if (!state) return 'waiting';
  switch (state) {
    case 'card-ordered':
    case 'card-order-failed':
    case 'awaiting-card-transfer':
      return 'card-ordered';
    case 'purchase-ordered':
    case 'purchase-failed':
    case 'unresolvable-relational-ticket':
    case 'unfulfillable':
      return 'purchase-ordered';
    case 'fulfilled':
      return 'fulfilled';
    default:
      return 'waiting';
  }
}

function isErrorState(
  memberState?: CollectionTicketClaimMemberStateDTO,
): boolean {
  if (!memberState) return false;
  return isClaimErrorState(memberState.state);
}

interface ClaimTimelineProps {
  memberState?: CollectionTicketClaimMemberStateDTO;
  inactivateAt?: string;
  showCardStep?: boolean;
}

export const ClaimTimeline = ({
  memberState,
  inactivateAt,
  showCardStep = false,
}: ClaimTimelineProps) => {
  const { formatMessage } = useIntl();
  const tenantId = useTenantID();

  const getStepLabel = useCallback(
    (step: TimelineStep): string => {
      if (step === 'waiting') return formatMessage(SCHEDULED_LABEL);
      return formatMessage(CLAIM_STATES[step].label);
    },
    [formatMessage],
  );

  const activeStepKey = stateToStepKey(memberState?.state);
  const steps = useMemo<readonly TimelineStep[]>(
    () =>
      showCardStep || activeStepKey === 'card-ordered'
        ? ALL_STEPS
        : ALL_STEPS.filter((step) => step !== 'card-ordered'),
    [activeStepKey, showCardStep],
  );
  const activeIndex = useMemo(() => {
    const id = steps.findIndex((s) => s === activeStepKey);
    return id >= 0 ? id : 0;
  }, [activeStepKey, steps]);

  const hasError = isErrorState(memberState);
  const otherTenant = findOtherTenantInClaimMessage(memberState, tenantId);
  const isUnfulfillable = memberState?.state === 'unfulfillable';
  const ticketWillBeRevoked =
    !!memberState &&
    !!inactivateAt &&
    claimStateRevokedBecauseStudentWillInactivate(memberState.state, {
      active: true,
      inactivate_at: inactivateAt,
    });
  const revocationReason =
    ticketWillBeRevoked && inactivateAt
      ? formatMessage(ticketRevocationMessages.reason, {
          date: formatDate(inactivateAt),
        })
      : undefined;

  const { updated_at } = memberState || {};

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {!isUnfulfillable && (
        <ol className="flex flex-wrap items-center gap-y-3">
          {steps.map((step, index) => {
            const isLastStep = index === steps.length - 1;
            const stepReached = index === activeIndex;
            const isCurrentError = stepReached && hasError;
            const isCurrentRevoked = stepReached && ticketWillBeRevoked;
            const isLastStepCompleted =
              isLastStep && stepReached && !isCurrentError && !isCurrentRevoked;
            const isCompleted = index < activeIndex || isLastStepCompleted;
            const isCurrent = stepReached && !isLastStepCompleted;
            const currentAndValid =
              isCurrent && !isCurrentError && !isCurrentRevoked;
            const futureStep = !isCompleted && !isCurrent;
            const label = isCurrentRevoked
              ? formatMessage(ticketRevocationMessages.label)
              : isCurrent && memberState?.state === 'awaiting-card-transfer'
                ? formatMessage(CLAIM_STATES['awaiting-card-transfer'].label)
                : getStepLabel(step);
            const stepState = isCompleted
              ? formatMessage({ defaultMessage: 'klar' })
              : isCurrentError
                ? formatMessage({ defaultMessage: 'fel' })
                : futureStep
                  ? formatMessage({ defaultMessage: 'kommande' })
                  : undefined;

            return (
              <li
                key={step}
                className="flex items-center"
                aria-current={isCurrent ? 'step' : undefined}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'relative flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                      isCompleted &&
                        'border-green-500 bg-green-500 text-contrast-green',
                      currentAndValid &&
                        'border-blue-600 bg-blue-50 text-blue-800',
                      isCurrentRevoked &&
                        'border-yellow-800 bg-yellow-100 text-yellow-800',
                      isCurrentError && 'border-red-700 bg-red-50 text-red-800',
                      futureStep &&
                        'border-text-600 bg-background-50 text-text-800',
                      isCurrent && currentAndValid && 'border-transparent',
                    )}
                    title={label}
                  >
                    {isCurrent && currentAndValid && (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'pointer-events-none absolute -inset-0.5 animate-spin-slow rounded-full border-2 border-dashed border-blue-600',
                        )}
                      />
                    )}
                    {isCompleted ? (
                      <IconCheck aria-hidden="true" />
                    ) : isCurrentError ? (
                      '!'
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={cn(
                      'mt-1.5 max-w-40 text-center text-sm leading-tight',
                      isCompleted && 'font-medium text-green-800',
                      currentAndValid && 'font-medium text-blue-800',
                      isCurrentRevoked && 'font-medium text-yellow-800',
                      isCurrentError && 'font-medium text-red-800',
                      futureStep && 'text-text-800',
                    )}
                  >
                    <span>{label}</span>
                    {stepState ? (
                      <span className="sr-only">, {stepState}</span>
                    ) : null}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className={cn(
                      'mb-5 h-0.5 w-12 shrink-0',
                      index < activeIndex ? 'bg-green-500' : 'bg-text-600',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
      <p
        className={cn(
          'mt-1',
          ticketWillBeRevoked
            ? 'text-yellow-800'
            : hasError
              ? 'text-red-600'
              : 'text-text-700',
        )}
      >
        {hasError && memberState && !ticketWillBeRevoked && (
          <span className="font-medium">
            {formatMessage(CLAIM_STATES[memberState.state].label)}:{' '}
          </span>
        )}
        {revocationReason ??
          (memberState
            ? formatMessage(CLAIM_STATES[memberState.state].description)
            : formatMessage(SCHEDULED_DESCRIPTION))}
      </p>
      {otherTenant && (
        <p className="text-red-600">
          <FormattedMessage
            defaultMessage="Länsöverskridande biljetter till {tenant} är inte korrekt konfigurerat hos Etis."
            values={{ tenant: otherTenant }}
          />
        </p>
      )}
      {hasError && (
        <p className="text-sm text-text-800 [overflow-wrap:anywhere]">
          <b>
            <FormattedMessage defaultMessage="Internt felmeddelande" />:
          </b>{' '}
          {memberState?.message}
        </p>
      )}
      {memberState && memberState.consecutive_failures > 0 && (
        <p className="text-sm text-text-700">
          <ClaimAttemptCount count={memberState.consecutive_failures} />
        </p>
      )}
      {updated_at && (
        <p className="text-sm text-text-700">
          <FormattedMessage defaultMessage="Uppdaterad" />{' '}
          {formatDateTime(updated_at)}
        </p>
      )}
    </div>
  );
};
